import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy
} from "firebase/firestore";
import Layout from "../components/Layout";

export default function SkuManager() {
  const [skus, setSkus] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadSkus = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "skuMaster"), orderBy("Name"));
      const snap = await getDocs(q);
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSkus(arr);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSkus();
  }, []);

  const stats = useMemo(() => {
    const brands = new Set(skus.map(s => s.Brand).filter(Boolean)).size;
    const totals = skus.length;
    return { brands, totals };
  }, [skus]);

  const handleCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split("\n").map((x) => x.trim()).filter(Boolean);
      if (lines.length < 2) return;

      const headers = lines[0].split(",").map(h => h.trim());
      const rows = lines.slice(1).map((line) => {
        const cols = line.split(",");
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = cols[i] ? cols[i].trim() : "";
        });
        return obj;
      });

      setCsvData(rows);
      setShowPreview(true);
    };
    reader.readAsText(file);
  };

  const confirmUpload = async () => {
    if (!window.confirm(`Import ${csvData.length} records into registry?`)) return;
    setLoading(true);
    try {
        for (const row of csvData) {
            await addDoc(collection(db, "skuMaster"), {
              clientId: row.clientId || "",
              ItemCode: row.ItemCode || "",
              sku: (row.sku || "").trim().toUpperCase(),
              Brand: row.Brand || "",
              Name: row.Name || "",
              description: row.description || "",
              msku: row.msku || "",
              segment: row.segment || "",
              hsn: row.hsn || "",
              Basic_Eway: Number(row.Basic_Eway || 0),
              distributorBasicRate: Number(row.distributorBasicRate || 0),
              dsBasicRate: Number(row.dsBasicRate || 0),
              gst: Number(row.gst || 0),
              cess: Number(row.cess || 0),
              addlcess: Number(row.addlcess || 0),
              Rebate: Number(row.Rebate || 0),
              openingstock: Number(row.openingstock || 0),
              mrp: Number(row.mrp || 0),
              skutype: row.skutype || "",
              sortorder: Number(row.sortorder || 0),
              swdorder: Number(row.swdorder || 0),
            });
          }
          alert("Manifest Provisioned Successfully!");
          setShowPreview(false);
          setCsvData([]);
          loadSkus();
    } catch (err) {
        alert("Upload error: " + err.message);
    } finally {
        setLoading(false);
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Archive this SKU from registry?")) return;
    await deleteDoc(doc(db, "skuMaster", id));
    loadSkus();
  };

  return (
    <Layout>
      <div className="layout-content">
        {/* HEADER */}
        <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
                <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>SKU Global Registry</h1>
                <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Manage the complete product master, tiered pricing, and bulk provisioning matrix.</p>
            </div>
            <div style={{ display: "flex", gap: 30 }}>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "var(--primary)" }}>{stats.totals}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Products</div>
                </div>
                <div style={{ width: 1, height: 40, background: "var(--border)" }}></div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "var(--success)" }}>{stats.brands}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Active Brands</div>
                </div>
            </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 32 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
                {/* PROVISIONING LOGIC (UPLOAD) */}
                <div className="premium-card" style={{ padding: 28 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 20, color: "var(--primary)", letterSpacing: "0.05em" }}>BULK PROVISIONING ENGINE</h3>
                    
                    <div style={{ 
                        background: "var(--surface-hover)", 
                        padding: 20, 
                        borderRadius: 12, 
                        border: "1px dashed var(--border)",
                        marginBottom: 24
                    }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text-muted)", marginBottom: 12, textTransform: "uppercase" }}>MANIFEST SPECIFICATION</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.6, fontFamily: "monospace", padding: "10px", background: "white", borderRadius: 8 }}>
                            clientId, ItemCode, sku, Brand, Name, hsn, Basic_Eway, dsBasicRate, mrp, gst, cess
                        </div>
                    </div>

                    <div style={{ position: "relative" }}>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleCSV}
                            style={{
                                cursor: "pointer",
                                opacity: 0,
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                zIndex: 2
                            }}
                        />
                        <div style={{ 
                            padding: "32px", 
                            border: "2px dashed var(--primary)", 
                            borderRadius: 16, 
                            textAlign: "center",
                            background: "var(--primary)05"
                        }}>
                            <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
                            <div style={{ fontWeight: 800, color: "var(--primary)" }}>Drop CSV manifest here or click to browse</div>
                            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Standard RFC 4180 format supported</div>
                        </div>
                    </div>
                </div>

                {/* PREVIEW MATRIX */}
                {showPreview && (
                    <div className="premium-card" style={{ padding: 0, overflow: "hidden", border: "2px solid var(--success)" }}>
                        <div style={{ padding: "16px 24px", background: "var(--success)10", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 12, fontWeight: 800, color: "var(--success)" }}>DATA VALIDATION PREVIEW • {csvData.length} RECORDS DETECTED</span>
                            <div style={{ display: "flex", gap: 12 }}>
                                <button onClick={() => setShowPreview(false)} className="btn-secondary" style={{ padding: "8px 16px", fontSize: 11 }}>Abort</button>
                                <button onClick={confirmUpload} className="btn-primary" style={{ padding: "8px 16px", fontSize: 11, background: "var(--success)" }}>Commit Changes</button>
                            </div>
                        </div>
                        <div style={{ maxHeight: 400, overflow: "auto" }}>
                            <table className="enterprise-table">
                                <thead>
                                    <tr>
                                        {Object.keys(csvData[0] || {}).map((h) => <th key={h}>{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {csvData.map((row, idx) => (
                                        <tr key={idx}>
                                            {Object.values(row).map((val, i) => <td key={i}>{val}</td>)}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* REGISTRY VIEW */}
                <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
                    <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-hover)", display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 12, fontWeight: 800 }}>ACTIVE PRODUCT REGISTRY</span>
                        {loading && <span style={{ fontSize: 11, color: "var(--primary)", fontWeight: 700 }}>REFRESHING ENGINE...</span>}
                    </div>
                    <div style={{ maxHeight: 800, overflow: "auto" }}>
                        <table className="enterprise-table">
                            <thead>
                                <tr>
                                    <th>Product Detail</th>
                                    <th>Tier Pricing</th>
                                    <th style={{ textAlign: "right" }}>Gst/Cess</th>
                                    <th style={{ textAlign: "center" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {skus.map((s) => (
                                    <tr key={s.id}>
                                        <td>
                                            <div style={{ fontWeight: 800, color: "var(--text-main)" }}>{s.Name}</div>
                                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.sku} • {s.Brand} • HSN: {s.hsn}</div>
                                        </td>
                                        <td>
                                            <div style={{ fontSize: 11 }}>Dist: <b>₹{s.distributorBasicRate}</b></div>
                                            <div style={{ fontSize: 11 }}>DS: <b>₹{s.dsBasicRate}</b></div>
                                            <div style={{ fontSize: 11, color: "var(--success)", fontWeight: 700 }}>MRP: ₹{s.mrp}</div>
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            <div style={{ fontWeight: 700 }}>{s.gst}%</div>
                                            <div style={{ fontSize: 10, color: "var(--text-muted)" }}>Cess: {s.cess}%</div>
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                            <button onClick={() => deleteOne(s.id)} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #fee2e2", background: "#fef2f2", color: "var(--danger)", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                                                ARCHIVE
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* SIDEBAR: HELP & DOCS */}
            <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
                <div className="premium-card">
                    <h4 style={{ fontSize: 12, fontWeight: 800, marginBottom: 16, color: "var(--text-muted)" }}>OPERATIONAL PROTOCOL</h4>
                    <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text-main)" }}>
                        <div style={{ marginBottom: 16 }}>• <b>Pricing Tier 1:</b> Distributor Base Rate is applied during primary supply billing.</div>
                        <div style={{ marginBottom: 16 }}>• <b>Pricing Tier 2:</b> DS Rate is applied when inventory is moved to salesman kits.</div>
                        <div style={{ marginBottom: 0 }}>• <b>Retail Matrix:</b> MRP and Basic_Eway define the market landing price.</div>
                    </div>
                </div>

                <div style={{ padding: 24, borderRadius: 20, background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", color: "white" }}>
                    <div style={{ fontSize: 24, marginBottom: 12 }}>💡</div>
                    <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 8 }}>Logistics Optimizer</div>
                    <p style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.5, margin: 0 }}>
                        Correct MSKU mapping ensures inventory consistency across multiple layers of the distribution network.
                    </p>
                </div>
            </div>
        </div>
      </div>
    </Layout>
  );
}

export const getAllSkus = async () => {
    const snap = await getDocs(collection(db, "skuMaster"));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};
  
export const getSkuByCode = async (code) => {
    const q = query(collection(db, "skuMaster"), where("ItemCode", "==", code));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() };
};
